/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Image,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import * as Styles from "./styles";
import { MAX_UPLOAD_BYTES } from "../constants";
import {
  AreaHighlight,
  FloorMap,
  TeleportInfo,
  Vector3,
} from "../../../../usecases/HomeConfig/types";
import { DraftFloorMap, DraftMapConfigNode } from "../types";
import { Vector3Input } from "../../../Vector3Input";

export interface MapNodeEditorProps {
  node: DraftMapConfigNode;
  showFloorName?: boolean;
  onChange: (updater: (node: DraftMapConfigNode) => DraftMapConfigNode) => void;
}

interface AreaField {
  label: string;
  field: "x" | "y" | "width" | "height";
}

interface AreaEntry {
  area: AreaHighlight;
  areaIndex: number;
}

interface FloorAreaGroup {
  floor: FloorMap;
  entries: AreaEntry[];
}

const AREA_FIELDS: AreaField[] = [
  { label: "X", field: "x" },
  { label: "Y", field: "y" },
  { label: "W", field: "width" },
  { label: "H", field: "height" },
];

const ZERO_TELEPORT: TeleportInfo = {
  location: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
};

const FILE_EXTENSION_PATTERN = /\.[^.]+$/;

const FLOOR_IMAGE_ACCEPT = "image/png,image/jpeg,image/svg+xml";

const rejectOversize = (files: RcFile[]): boolean => {
  const hasOversize = files.some((picked) => {
    return picked.size > MAX_UPLOAD_BYTES;
  });
  if (hasOversize) {
    message.warning("Each floor image must be 5MB or smaller");
  }
  return hasOversize;
};

// NOTE: One url per File — createObjectURL during render mints one per keystroke.
const previewUrlCache = new WeakMap<File, string>();

const getPreviewUrl = (floor: DraftFloorMap): string => {
  if (!floor.imageFile) {
    return floor.imageUrl;
  }
  const cached = previewUrlCache.get(floor.imageFile);
  if (cached) {
    return cached;
  }
  const blobUrl = URL.createObjectURL(floor.imageFile);
  previewUrlCache.set(floor.imageFile, blobUrl);
  return blobUrl;
};

export const MapNodeEditor = ({
  node,
  showFloorName = false,
  onChange,
}: MapNodeEditorProps) => {
  const patchArea = (rowIndex: number, patch: Partial<AreaHighlight>) => {
    onChange((current) => {
      const areaHighlights = current.areaHighlights.map((area, itemIndex) => {
        if (itemIndex !== rowIndex) {
          return area;
        }
        return { ...area, ...patch };
      });
      return { ...current, areaHighlights };
    });
  };

  const patchFloor = (rowIndex: number, patch: Partial<DraftFloorMap>) => {
    onChange((current) => {
      const floorMaps = current.floorMaps.map((floor, itemIndex) => {
        if (itemIndex !== rowIndex) {
          return floor;
        }
        return { ...floor, ...patch };
      });
      return { ...current, floorMaps };
    });
  };

  // NOTE: antd calls beforeUpload once per file; the whole batch arrives as
  // `fileList` on the first call, so later calls would duplicate the rows.
  const handlePickFloorImages = (file: RcFile, fileList: RcFile[]) => {
    if (file !== fileList[0]) {
      return false;
    }
    if (rejectOversize(fileList)) {
      return false;
    }
    onChange((current) => {
      const usedIndexes = current.floorMaps.map((item) => {
        return item.floorIndex;
      });
      const highestIndex =
        usedIndexes.length === 0 ? -1 : Math.max.apply(null, usedIndexes);
      const added = fileList.map((picked, offset) => {
        return {
          floorIndex: highestIndex + 1 + offset,
          floorName: picked.name.replace(FILE_EXTENSION_PATTERN, ""),
          imageUrl: "",
          imageFile: picked,
        };
      });
      return { ...current, floorMaps: [...current.floorMaps, ...added] };
    });
    return false;
  };

  const addAreaToFloor = (floorIndex: number) => {
    onChange((current) => {
      const added: AreaHighlight = {
        floorIndex,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      };
      return { ...current, areaHighlights: [...current.areaHighlights, added] };
    });
  };

  const areaGroups: FloorAreaGroup[] = node.floorMaps.map((floor) => {
    const entries: AreaEntry[] = [];
    node.areaHighlights.forEach((area, areaIndex) => {
      if (area.floorIndex === floor.floorIndex) {
        entries.push({ area, areaIndex });
      }
    });
    return { floor, entries };
  });

  // NOTE: Areas authored before floorIndex existed match no floor, so surface
  // them in their own panel — otherwise their data is invisible and unremovable.
  const orphanEntries: AreaEntry[] = [];
  node.areaHighlights.forEach((area, areaIndex) => {
    const hasFloor = node.floorMaps.some((floor) => {
      return floor.floorIndex === area.floorIndex;
    });
    if (!hasFloor) {
      orphanEntries.push({ area, areaIndex });
    }
  });

  const renderFloorRow = (floor: DraftFloorMap, rowIndex: number) => {
    const handleRemove = () => {
      onChange((current) => {
        return {
          ...current,
          floorMaps: current.floorMaps.filter((_, itemIndex) => {
            return itemIndex !== rowIndex;
          }),
          areaHighlights: current.areaHighlights.filter((area) => {
            return area.floorIndex !== floor.floorIndex;
          }),
        };
      });
    };

    // NOTE: floorIndex is the floor's identity, so the model must never see a
    // half-typed value — commit on blur and reject blanks or duplicates.
    const handleFloorIndexCommit = (
      event: React.FocusEvent<HTMLInputElement>,
    ) => {
      const rawValue = event.target.value.trim();
      const nextIndex = Number(rawValue);
      if (!rawValue || !Number.isInteger(nextIndex)) {
        message.warning("Floor index must be a whole number");
        return;
      }
      if (nextIndex === floor.floorIndex) {
        return;
      }
      const isDuplicate = node.floorMaps.some((item, itemIndex) => {
        return itemIndex !== rowIndex && item.floorIndex === nextIndex;
      });
      if (isDuplicate) {
        message.warning("Floor index must be unique");
        return;
      }
      const previousIndex = floor.floorIndex;
      onChange((current) => {
        const floorMaps = current.floorMaps.map((item, itemIndex) => {
          if (itemIndex !== rowIndex) {
            return item;
          }
          return { ...item, floorIndex: nextIndex };
        });
        const areaHighlights = current.areaHighlights.map((area) => {
          if (area.floorIndex !== previousIndex) {
            return area;
          }
          return { ...area, floorIndex: nextIndex };
        });
        return { ...current, floorMaps, areaHighlights };
      });
    };

    const handleFloorNameChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      patchFloor(rowIndex, { floorName: event.target.value });
    };

    const handleReplaceImage = (file: RcFile) => {
      if (rejectOversize([file])) {
        return false;
      }
      patchFloor(rowIndex, { imageFile: file });
      return false;
    };

    const previewUrl = getPreviewUrl(floor);

    const floorNameItem = showFloorName ? (
      <Form.Item label="Name">
        <Input
          css={Styles.textInputStyle}
          value={floor.floorName}
          onChange={handleFloorNameChange}
        />
      </Form.Item>
    ) : null;

    return (
      <Card
        size="small"
        css={Styles.rowStyle}
        key={rowIndex}
        title={`Floor ${rowIndex + 1}`}
        extra={
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemove}
          />
        }
      >
        <Space size="middle" wrap align="start">
          <Form.Item label="Index">
            <Input
              type="number"
              css={Styles.floorSelectStyle}
              defaultValue={floor.floorIndex}
              onBlur={handleFloorIndexCommit}
            />
          </Form.Item>
          {floorNameItem}
          <Form.Item label="Image">
            <Space size="small" wrap align="start">
              <Image width={96} height={64} src={previewUrl} />
              <Upload
                accept={FLOOR_IMAGE_ACCEPT}
                showUploadList={false}
                beforeUpload={handleReplaceImage}
              >
                <Button icon={<UploadOutlined />}>REPLACE</Button>
              </Upload>
            </Space>
          </Form.Item>
        </Space>
      </Card>
    );
  };

  const renderAreaRow = (entry: AreaEntry) => {
    const { area, areaIndex } = entry;

    const handleRemove = () => {
      onChange((current) => {
        const areaHighlights = current.areaHighlights.filter((_, itemIndex) => {
          return itemIndex !== areaIndex;
        });
        return { ...current, areaHighlights };
      });
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      patchArea(areaIndex, { name: event.target.value });
    };

    const numberItems = AREA_FIELDS.map((areaField) => {
      const handleValueChange = (value: number | null) => {
        patchArea(areaIndex, { [areaField.field]: value ?? 0 });
      };

      return (
        <Form.Item label={areaField.label} key={areaField.field}>
          <InputNumber
            min={0}
            max={100}
            addonAfter="%"
            value={area[areaField.field]}
            onChange={handleValueChange}
          />
        </Form.Item>
      );
    });

    const teleport = area.teleport ?? ZERO_TELEPORT;

    const handleChangeTeleportLocation = (location: Vector3) => {
      patchArea(areaIndex, { teleport: { ...teleport, location } });
    };

    const handleChangeTeleportRotation = (rotation: Vector3) => {
      patchArea(areaIndex, { teleport: { ...teleport, rotation } });
    };

    return (
      <Col span={24} key={areaIndex}>
        <Card
          size="small"
          css={Styles.rowStyle}
          title={area.name?.trim() || `Area ${areaIndex + 1}`}
          extra={
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
            />
          }
        >
          <Space direction="vertical" size="small" css={Styles.areaStackStyle}>
            <Space size="middle" wrap align="end">
              <Form.Item label="Name">
                <Input
                  css={Styles.textInputStyle}
                  value={area.name}
                  onChange={handleNameChange}
                />
              </Form.Item>
              {numberItems}
            </Space>
            <Vector3Input
              label="Teleport Location"
              value={teleport.location}
              onChange={handleChangeTeleportLocation}
            />
            <Vector3Input
              label="Teleport Rotation"
              value={teleport.rotation}
              onChange={handleChangeTeleportRotation}
            />
          </Space>
        </Card>
      </Col>
    );
  };

  const renderFloorAreaPanel = (group: FloorAreaGroup) => {
    const handleAdd = () => {
      addAreaToFloor(group.floor.floorIndex);
    };

    return (
      <Collapse.Panel
        key={String(group.floor.floorIndex)}
        header={group.floor.floorName}
      >
        <Space direction="vertical">
          {group.entries.map(renderAreaRow)}
          <Button icon={<PlusOutlined />} onClick={handleAdd}>
            ADD AREA
          </Button>
        </Space>
      </Collapse.Panel>
    );
  };

  const areaPanels = areaGroups.map(renderFloorAreaPanel);
  if (orphanEntries.length > 0) {
    areaPanels.push(
      <Collapse.Panel key="unassigned" header="Unassigned">
        <Row gutter={12}>{orphanEntries.map(renderAreaRow)}</Row>
      </Collapse.Panel>,
    );
  }

  return (
    <Form layout="vertical">
      <Row gutter={[0, 16]} css={Styles.containerStyle}>
        <Col span={24}>
          <Typography.Text type="secondary" css={Styles.captionStyle}>
            Floor Maps
          </Typography.Text>
          <Upload
            multiple
            accept={FLOOR_IMAGE_ACCEPT}
            showUploadList={false}
            beforeUpload={handlePickFloorImages}
          >
            <Button icon={<PlusOutlined />}>ADD FLOOR IMAGES</Button>
          </Upload>
          {node.floorMaps.map(renderFloorRow)}
        </Col>
        <Col span={24}>
          <Typography.Text type="secondary" css={Styles.captionStyle}>
            Area Highlights
          </Typography.Text>
          <Collapse>{areaPanels}</Collapse>
        </Col>
      </Row>
    </Form>
  );
};
